import { Component, ElementRef, OnInit, Input } from '@angular/core';
import { GameService } from 'src/app/shared/services/game/game.service';
import { GAMETYPES, PRIZETYPES, PRIZESTATUS } from 'src/app/shared/constants/constants';
import { GameMessageType, GameModalMessage, MyPrize, PrizeDetail } from 'src/app/shared/constants/divisional.interface';
import { properties } from 'src/environments/properties';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-my-prize-list',
  templateUrl: './my-prize-list.component.html',
  styleUrls: ['./my-prize-list.component.scss']
})
export class MyPrizeListComponent implements OnInit {

  @Input()
  public gameId: string = "";
  @Input()
  public userInfo: any;

  public noPrizeMessage: string =  properties.noPrizeMessage;
  public noPrizeAdditionalMessage: string = properties.playNow;
  public showList: boolean = false;
  public myPrizes: MyPrize[] = [];
  public loading: boolean = false;
  public itemsVisible: number = 3;

  public sm = '575px';
  public md = '768px';
  public imagePath: string;

  public displayPrizeCount: number = 3;
  public maxDaysRemaining: number = 365;
  public userOptedIn: boolean = false;
  public gameType: string = null;
  public GAMETYPES = GAMETYPES;
  public PRIZETYPES = PRIZETYPES;
  public PRIZESTATUS = PRIZESTATUS;
  private currentEnvironment: string;
  private destroy$ = new Subject<void>();

  constructor(public elementRef: ElementRef, public gameService: GameService) {

  }

  ngOnInit(): void {
    this.currentEnvironment = this.gameService.getEnvironment();
    this.imagePath = environment[this.currentEnvironment].imagePath;
    this.gameService.refreshGameDetailsObservable.pipe(takeUntil(this.destroy$)).subscribe(details => {
      this.userOptedIn = details.userOptedIn;
      this.gameType = details.type;
      if (details.codes == this.gameId && this.gameService.isAuthenticated(this.userInfo) && ( this.gameService.optinEnhancements || this.userOptedIn)) {
        this.gameId = details.codes;
        this.updateMyPrizes();
      } else {
        this.showList = true;
      }
    });

    this.gameService.refreshOptInStatusOnlyObservable.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data) {
          this.userOptedIn = true;
      }
    })

    this.gameService.prizeRedeemedObservable.subscribe(flag => {
      this.updateMyPrizes();
    });

  }

  public updateMyPrizes() {

    this.loading = true;
    this.gameService.getMyPrizes(this.gameId, this.userInfo).subscribe(response => {
      this.loading = false;
      if (response && response.prizeTransactions) {
        this.myPrizes = response.prizeTransactions;
      } else {
        this.myPrizes = [];
      }
      this.showList = true;
    }, error => {
      this.loading = false;
      this.myPrizes = [];
      this.showList = true;
    });

  }

  public getDaysRemaining(prize: MyPrize): number {
    if (prize.prizeType == PRIZETYPES.SWEEPSTAKES && prize.sweepstakeInfo) {
      let daysLeft = prize.sweepstakeInfo.drawingDaysLeft;
      return daysLeft <= 99 && daysLeft >= 1 ? daysLeft : 0;
    }
    return 0;
  }

  public getDaysLabel(days: number): string {
    return (days == 1) ? "day" : "days";
  }

  public getDisplayDate(prize: MyPrize): Date {
    let displayDate: Date = null;
    // GC-1363 always use purchase date
    displayDate = new Date(prize.purchaseDate.split(' ')[0]);

    return displayDate;
  }

  public getImageUrl(prize: MyPrize, viewport: string): string {
    let id = prize.prizeId;
    if (prize.prizeType == PRIZETYPES.B4U_OFFER && prize.imageId) {
      id = prize.imageId;
    }
    const modifiedImagePath = this.imagePath;
    return modifiedImagePath ? modifiedImagePath.replace('${product.id}', id).replace('${viewport}', viewport) : '';
  }

  public showMorePrizes(): void {
    let nextPrize = this.displayPrizeCount;
    this.displayPrizeCount += this.itemsVisible;
    if (window['AB'] && window['AB'].DATALAYER) {
      window['AB'].DATALAYER.setLinkClickEvents(`games|${this.gameId}:redemptions`);
    }
    let prizeItems: any = document.getElementsByClassName('prize-link-anchor');
    setTimeout(() => {
      if (prizeItems && prizeItems[nextPrize]) {
        prizeItems[nextPrize].focus();
      }
    }, 100);
  }

  public getStatusLine(prize: MyPrize): string {
    let statusLine = '';
    if (prize.prizeType == PRIZETYPES.REWARD) {
      if (prize.prizeFulfillmentStatus.includes("Rewards")) {
        statusLine += prize.prizeFulfillmentStatus.replace(/Rewards/g, "");
      } else {
        statusLine += prize.prizeFulfillmentStatus;
      }
    } else if (prize.prizeType == PRIZETYPES.SWEEPSTAKES && prize.status == PRIZESTATUS.COMPLETED) {
      let sweepstakeInfo = prize.sweepstakeInfo;
      if (sweepstakeInfo) {
        // always display the subtext
        statusLine += sweepstakeInfo.subtext;
      }
    } else {
      statusLine = prize.prizeFulfillmentStatus;
    }
    return statusLine.trim();
  }

  public getHrefToRedirect(prize: MyPrize): string {
    let redirectToHref = '';
    switch (prize.prizeType) {
      case PRIZETYPES.B4U_OFFER:
        redirectToHref += '/mylist.html';
        break;
      case PRIZETYPES.REWARD:
        redirectToHref += '/foru/rewards.html';
        break;
      case PRIZETYPES.FRESH_PASS:
        redirectToHref += '/freshpass/manage/subscribe.html';
        break;
      case PRIZETYPES.RECIPE:
        redirectToHref += '/meal-plans-recipes';
        break;
      default:
        redirectToHref += '';
        break;
    }
    return redirectToHref;
  }

  public getViewAriaLabel(prize: MyPrize): string {
    let labelText = '';
    switch (prize.prizeType) {
      case PRIZETYPES.B4U_OFFER:
        labelText += 'View My List';
        break;
      case PRIZETYPES.REWARD:
        labelText += 'View My Rewards';
        break;
      case PRIZETYPES.FRESH_PASS:
        labelText += 'View information on Fresh Pass';
        break;
      case PRIZETYPES.RECIPE:
        labelText += 'View Recipes';
        break;
      default:
        labelText += '';
        break;
    }
    return labelText;
  }

  public getPrizeType(prize: MyPrize): boolean {
    return prize?.prizeType !== 'ADD_TO_CART' ? true : false;
  }

  public getTitle() {
    let title = 'My Prizes';
    if (this.gameType == GAMETYPES.EPISODIC) {
      title = 'Gameplay History';
    } else if (this.gameType == GAMETYPES.DIVISION) {
      title = 'Redemptions';
    }
    return title;
  }

  public showSweepstakesModal(prize: PrizeDetail) {
    let message: GameModalMessage = {
      mode: GameMessageType.SHOWSWEEPSTAKES,
      gameId: this.gameId,
      gameName: '',
      prizeDetail: prize
    }
    this.gameService.gameModalMessage.next(message);
  }

  ngOnDestroy() {
    this.destroy$.next();  // This will trigger the unsubscription
    this.destroy$.complete();
  }
}
